import { AuthService } from '@skinsoft/auth';
import { User } from '@skinsoft/auth';
import {
    EntityDataStore,
    LinkableItem,
    LinkableItemLifecycleStates,
    WorkflowSchema,
} from '@skinsoft/entity-data-store';
import { cloneDeep, set } from 'lodash';
import { concatMap, first } from 'rxjs/operators';

import { Item } from '../item/item-data-store';
import { Location } from '../location/location-data-store';
import * as taskSelectors from '../task/selectors';
import { Task, TaskDataStore } from '../task/task-data-store';
import * as movementSelectors from './selectors';
import { Collaborator, AliasDataStore as CollaboratorDataStore } from '@skinsoft/entity';

interface DateContainer {
    startInterval: {
        date: Date;
    };
}

export enum MovementTypes {
    COMPLEX = 'complex',
    RETURN = 'returning',
    SIMPLE = 'simple',
}

export enum MovementLifecycleStates {
    ARCHIVED = 'archived',
    CANCELED = 'canceled',
    FINALIZED = 'finalized',
    IN_PREPARATION = 'inPreparation',
    IN_PROGRESS = 'inProgress',
    PROGRAMMED = 'programmed',
}

export interface Movement {
    id: string;
    movementSchema: {
        affectedStorekeepers: string[];
        plannedDate: {
            endDate: DateContainer;
            startDate: DateContainer;
        };
    };
    movementType: MovementTypes;
    workflowSchema: WorkflowSchema<MovementLifecycleStates>;
}

export class MovementDataStore extends EntityDataStore<Movement, MovementLifecycleStates> {
    public static highlightStates = [MovementLifecycleStates.PROGRAMMED];
    public static featuredStates = [
        MovementLifecycleStates.IN_PREPARATION,
        MovementLifecycleStates.IN_PROGRESS,
        MovementLifecycleStates.PROGRAMMED,
    ]; // the order matters, it is used in the featured lists
    public static historyStates = [MovementLifecycleStates.FINALIZED];
    public static sortFields = ['movementSchema.effectiveDates.startDate.startInterval.date'];

    public static getInstance() {
        if (!MovementDataStore.instance) {
            MovementDataStore.instance = new MovementDataStore();
        }

        return MovementDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$movementDataStore = MovementDataStore.getInstance();
    }

    private static instance: MovementDataStore = null as any;

    constructor() {
        super('movements');
    }

    public async closeMovement(
        movementId: Movement['id'],
        arrivalLocation: string = null as any,
        modifyPermanentLocation: boolean = false,
    ) {
        let movement = await this.getMovement(movementId);

        if (movementSelectors.movementType(movement) === MovementTypes.SIMPLE) {
            if (!arrivalLocation) {
                throw new Error('You must provide an arrival location');
            }

            movement = set(movement, ['movementSchema', 'arrivalLocation'], arrivalLocation);
            movement = set(
                movement,
                ['workflowSchema', 'modifyPermanentLocation'],
                modifyPermanentLocation,
            );

            const linkableItems = movementSelectors.linkableItems(movement);
            linkableItems
                .filter(
                    linkableItem =>
                        linkableItem.state.name === LinkableItemLifecycleStates.IN_TRANSIT,
                )
                .forEach(linkableItem => {
                    linkableItem.arrivalLocation = arrivalLocation;
                    linkableItem.modifyPermanentLocation = modifyPermanentLocation;
                    linkableItem.state.name = LinkableItemLifecycleStates.FINALIZED;
                });

            await this.update(movement.id, movement)
                .pipe(first())
                .toPromise();
        }

        return this.transitionMovement(movement.id, MovementLifecycleStates.FINALIZED);
    }

    public cancelMovement(movementId: Movement['id']) {
        return this.transitionMovement(movementId, MovementLifecycleStates.CANCELED);
    }

    public async createReturnMovement(movementId: Movement['id']) {
        const movement = await this.getMovement(movementId);

        const movementType = movementSelectors.movementType(movement)

        if (movementType === 'returning') {
            throw new Error('You cannot create a return movement from a return movement');
        }

        const user = await AuthService.getInstance()
            .user$.pipe(first())
            .toPromise();
        const collaborator = await CollaboratorDataStore.getInstance().getCollaboratorByUserId((user as User).id).toPromise();


        const returnMovement = await this.proxy
            .fetch(`/movements/return`, { movementId: movement.id, typeMovementReturn: movementType }, 'POST')
            .toPromise();


        // Setting current collaborator to storekeeper field    
        returnMovement.movementSchema.affectedStorekeepers = [(collaborator as Collaborator).id];
        await this.update(movement.id, returnMovement).pipe(first()).toPromise();

        return await this.transitionMovement(returnMovement.id, MovementLifecycleStates.PROGRAMMED);
    }

    public async addItem(
        movementId: Movement['id'],
        itemId: Item['id'],
        itemType: string,
        arrivalLocation: string = null as any,
    ) {
        const movement = await this.getMovement(movementId);

        const linkableItems = movementSelectors.linkableItems(movement) || [];
        linkableItems.push({
            arrivalLocation,
            linkableItem: itemId,
            linkableItemReferenceType: itemType,
            state: {
                name: LinkableItemLifecycleStates.IN_TRANSIT,
            },
        } as LinkableItem);

        movement.workflowSchema.linkableItems = linkableItems;

        await this.update(movement.id, movement)
            .pipe(first())
            .toPromise();

        switch (movementSelectors.state(movement)) {
            case MovementLifecycleStates.IN_PREPARATION:
                await this.transitionMovement(movement.id, MovementLifecycleStates.PROGRAMMED);
                await this.transitionMovement(movement.id, MovementLifecycleStates.IN_PROGRESS);
                break;
            case MovementLifecycleStates.PROGRAMMED:
                await this.transitionMovement(movement.id, MovementLifecycleStates.IN_PROGRESS);
                break;
        }
    }

    public async addItems(movementId, newLinkableItems, arrivalLocation: string = null as any) {

        const movement = await this.getMovement(movementId);

        const linkableItems = movementSelectors.linkableItems(movement) || [];

        newLinkableItems.map(
            (item) => {
                linkableItems.push({
                    arrivalLocation,
                    linkableItem: item.linkableItem,
                    linkableItemReferenceType: item.linkableItemReferenceType,
                    state: {
                        name: LinkableItemLifecycleStates.SELECTED,
                    },
                } as LinkableItem)

            }
        )

        movement.workflowSchema.linkableItems = linkableItems;

        await this.update(movement.id, movement)
            .pipe(first())
            .toPromise();

        switch (movementSelectors.state(movement)) {
            case MovementLifecycleStates.IN_PREPARATION:
                await this.transitionMovement(movement.id, MovementLifecycleStates.PROGRAMMED);
                await this.transitionMovement(movement.id, MovementLifecycleStates.IN_PROGRESS);
                break;
            case MovementLifecycleStates.PROGRAMMED:
                await this.transitionMovement(movement.id, MovementLifecycleStates.IN_PROGRESS);
                break;
        }

    }

    public async createMovement(
        movementType: MovementTypes.SIMPLE | MovementTypes.COMPLEX,
        taskId?: Task['id'],
        title?: string,
        motive?: string,
    ) {
        const user = await AuthService.getInstance()
            .user$.pipe(first())
            .toPromise();

        const task = taskId
            ? await TaskDataStore.getInstance()
                .fetchOne(taskId, true)
                .pipe(first())
                .toPromise()
            : null;

        const collaborator = await CollaboratorDataStore.getInstance().getCollaboratorByUserId((user as User).id).toPromise();

        const movement = await this.create(
            {
                movementType,
            } as Partial<Movement>,
            {
                movementSchema: {
                    affectedStorekeepers: [(collaborator as Collaborator).id],
                    ...((title || motive) && {
                        ...(title && { title: { translatedString: { fr: title } } }),
                        ...(motive && { movementMotif: motive }),
                    }),
                    ...(task && { associatedTasks: [taskId] }),
                },
            } as Partial<Movement>,
        )
            .pipe(first())
            .toPromise();

        if (!task) {
            return movement;
        }

        const linkableItems = taskSelectors.linkableItems(task);

        await this.addItems(movement.id, linkableItems);

        return this.fetchOne(movement.id, true)
            .pipe(first())
            .toPromise();
    }

    public async transitionLinkableItem(
        movementId: Movement['id'],
        linkableItemId: string,
        state: LinkableItemLifecycleStates,
        arrivalLocation?: Location['id'],
    ) {
        const movement = await this.getMovement(movementId);

        const linkableItems = movementSelectors.linkableItems(movement);
        const index = linkableItems.findIndex(i => i.linkableItem === linkableItemId);

        set(movement, ['workflowSchema', 'linkableItems', index, 'state', 'name'], state);

        if (movementSelectors.movementType(movement) !== MovementTypes.SIMPLE && arrivalLocation) {
            set(
                movement,
                ['workflowSchema', 'linkableItems', index, 'arrivalLocation'],
                arrivalLocation,
            );
        }

        const updatedMovement = await this.update(movementId, movement)
            .pipe(first())
            .toPromise();

        if (movementSelectors.state(movement) === MovementLifecycleStates.PROGRAMMED) {
            return await this.transitionMovement(movementId, MovementLifecycleStates.IN_PROGRESS);
        }

        return updatedMovement;
    }

    private transitionMovement(
        movementId: Movement['id'],
        lifecycleState: MovementLifecycleStates,
    ) {
        return this.proxy
            .fetch(`/movements/tracking`, { id: movementId, lifecycleState }, 'PATCH')
            .pipe(
                concatMap(() => this.fetchOne(movementId, true)),
                first(),
            )
            .toPromise();
    }

    private async getMovement(id: Movement['id']) {
        return cloneDeep(
            await this.fetchOne(id, true)
                .pipe(first())
                .toPromise(),
        );
    }
}
